<template>
  <BaseSlideOverModal v-model:is-modal-open="localIsModalOpen" width="2xl">
    <template #cta>
      <BaseButton
        v-if="addWikiPlaceTab"
        :title="$t('button.create_new_wiki_place')"
        :icon="'plus'"
        color="paper-plane"
        inverse-icon-order
        :disabled="
          !(
            newWikiPlace.city_id &&
            newWikiPlace.address_id &&
            newWikiPlace.type
          ) || savingNewWikiPlace
        "
        :display-loader="savingNewWikiPlace"
        @click="saveNewWikiPlace"
      />
    </template>
    <template #body>
      <div class="flex h-full w-full flex-col gap-3">
        <h2
          v-if="eventType"
          class="w-auto rounded bg-primary-500 p-2 text-xl font-medium text-white capitalize-first hover:bg-primary-500 hover:text-white xl:sticky dark:text-blue-900"
        >
          {{ $t(`enums.event.type.${eventType}`) }}
        </h2>
        <div v-if="placeGroupsList.length > 1" class="flex-shrink-0">
          <SelectPlaceGroup
            v-model="selectedPlaceGroup"
            :place-groups-list="placeGroupsList"
          />
        </div>
        <div class="min-h-0 flex-1">
          <SelectDossierPlace
            v-if="selectedPlaceGroup === 'dossier_place' && dossierId"
            v-model:place-id="localPlaceId"
            v-model:place-type="localPlaceType"
            v-model:place-model="localPlaceModel"
            v-model:place-other="localPlaceOther"
            v-model:place-street="localPlaceStreet"
            v-model:place-extra-street="localPlaceExtraStreet"
            v-model:place-city="localPlaceCity"
            v-model:place-zip="localPlaceZip"
            v-model:place-city-id="localPlaceCityId"
            v-model:place-country="localPlaceCountry"
            v-model:place-city-object="localPlaceCityObject"
            :dossier-id="dossierId"
            :available-place-type="placeDossierTypeAvailable"
            @close="close"
          />
          <SelectFuneralHomePlace
            v-else-if="selectedPlaceGroup === 'company_place'"
            v-model:place-id="localPlaceId"
            v-model:place-type="localPlaceType"
            v-model:place-model="localPlaceModel"
            :available-place-type="placeCompanyAvailable"
            :default-place-group="defaultPlaceGroup"
            :default-place-type="defaultPlaceType"
            @close="close"
          />
          <SelectWikiPlace
            v-else-if="selectedPlaceGroup === 'wiki_place'"
            ref="wikiPlaceTab"
            v-model:place-id="localPlaceId"
            v-model:place-type="localPlaceType"
            v-model:place-model="localPlaceModel"
            v-model:add-wiki-place-tab="addWikiPlaceTab"
            v-model:new-wiki-place="newWikiPlace"
            :latitude="latitude"
            :longitude="longitude"
            :available-place-type="wikiPlaceAvailable"
            :default-place-group="defaultPlaceGroup"
            :default-place-type="defaultPlaceType"
            @close="close"
          />
          <SelectOtherPlace
            v-else-if="selectedPlaceGroup === 'other_place'"
            v-model:place-id="localPlaceId"
            v-model:place-type="localPlaceType"
            v-model:place-model="localPlaceModel"
            v-model:place-other="localPlaceOther"
            v-model:place-street="localPlaceStreet"
            v-model:place-extra-street="localPlaceExtraStreet"
            v-model:place-city="localPlaceCity"
            v-model:place-zip="localPlaceZip"
            v-model:place-city-id="localPlaceCityId"
            v-model:place-country="localPlaceCountry"
            v-model:place-city-object="localPlaceCityObject"
            :available-place-type="otherPlaceAvailable"
            :required-city="requiredCity"
            @close="close"
          />
        </div>
      </div>
    </template>
  </BaseSlideOverModal>
</template>

<script>
import BaseSlideOverModal from '@c/addf-package/components/modal/BaseSlideOverModal'
import PlaceType from '@/assets/enums/config/PlaceType'
import SelectPlaceGroup from '@ui/modal/place/components/SelectPlaceGroup.vue'
import SelectDossierPlace from '@ui/modal/place/components/SelectDossierPlace.vue'
import SelectFuneralHomePlace from '@ui/modal/place/components/SelectFuneralHomePlace.vue'
import SelectWikiPlace from '@ui/modal/place/components/SelectWikiPlace.vue'
import SelectOtherPlace from '@ui/modal/place/components/SelectOtherPlace.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import { mapActions } from 'vuex'

function emptyWikiPlace(locale) {
  return {
    name: {
      [locale]: ''
    },
    address_id: null,
    address: null,
    city_id: null,
    city: null,
    zip: null,
    city_object: null
  }
}

export default {
  name: 'SelectPlaceModal',
  components: {
    BaseButton,
    SelectOtherPlace,
    SelectWikiPlace,
    SelectFuneralHomePlace,
    SelectDossierPlace,
    SelectPlaceGroup,
    BaseSlideOverModal
  },
  props: {
    isModalOpen: {
      type: Boolean,
      required: true
    },
    placeModel: {
      type: Object,
      required: true
    },
    placeType: {
      type: String,
      required: true
    },
    placeId: {
      type: [String, Number],
      required: true
    },
    placeOther: {
      type: String,
      required: false,
      default: null
    },
    placeStreet: {
      type: String,
      required: false,
      default: null
    },
    placeExtraStreet: {
      type: String,
      required: false,
      default: null
    },
    placeZip: {
      type: String,
      required: false,
      default: null
    },
    placeCity: {
      type: String,
      required: false,
      default: null
    },
    placeCityObject: {
      type: Object,
      required: false,
      default: () => {}
    },
    placeCountry: {
      type: String,
      required: false,
      default: null
    },
    dossierId: {
      type: String,
      required: false,
      default: null
    },
    suggestPlaceOfDossier: {
      type: Boolean,
      required: false,
      default: false
    },
    suggestPlaceOfCompany: {
      type: Boolean,
      required: false,
      default: false
    },
    availablePlaceType: {
      type: Array,
      required: true
    },
    defaultPlaceGroup: {
      type: String,
      required: false,
      default: null
    },
    defaultPlaceType: {
      type: String,
      required: false,
      default: null
    },
    latitude: {
      type: Number,
      required: false,
      default: null
    },
    longitude: {
      type: Number,
      required: false,
      default: null
    },
    requiredCity: {
      type: Boolean,
      required: false,
      default: false
    },
    eventType: {
      type: String,
      required: false,
      default: null
    }
  },
  emits: [
    'close',
    'change',
    'update:isModalOpen',
    'update:placeType',
    'update:placeId',
    'update:placeName',
    'update:placeModel',
    'update:placeZip',
    'update:placeCountry',
    'update:placeCityObject',
    'update:placeCityId',
    'update:placeCity',
    'update:placeExtraStreet',
    'update:placeStreet',
    'update:placeOther'
  ],
  data() {
    return {
      selectedPlaceGroup: this.defaultPlaceGroup,
      selectedPlaceType: this.defaultPlaceType,
      addWikiPlaceTab: false,
      savingNewWikiPlace: false,
      newWikiPlace: emptyWikiPlace(this.$i18n.locale)
    }
  },
  computed: {
    localIsModalOpen: {
      get() {
        return this.isModalOpen
      },
      set(value) {
        this.$emit('update:isModalOpen', value)
      }
    },
    placeDossierTypeAvailable() {
      return this.availablePlaceType
      // return [PlaceType.DOMICILE, PlaceType.LIEU_DECES, PlaceType.RUE].filter(
      //   (item) => this.availablePlaceType.includes(item)
      // )
    },
    placeCompanyAvailable() {
      return [PlaceType.FUNERARIUM, PlaceType.SALLE_FUNERARIUM].filter((item) =>
        this.availablePlaceType.includes(item)
      )
    },
    wikiPlaceAvailable() {
      return [
        PlaceType.CIMETIERE,
        PlaceType.EGLISE,
        PlaceType.CREMATORIUM,
        PlaceType.HOPITAL,
        PlaceType.MAISON_DE_REPOS,
        PlaceType.SALLE_CREMATORIUM,
        PlaceType.WIKI_FUNERARIUM,
        PlaceType.WIKI_SALLE_CEREMONIE,
        PlaceType.WIKI_SALLE_DE_RECEPTION
      ].filter((item) => this.availablePlaceType.includes(item))
    },
    otherPlaceAvailable() {
      return [PlaceType.RUE, PlaceType.AUTRES].filter((item) =>
        this.availablePlaceType.includes(item)
      )
    },
    placeGroupsList() {
      let group = []
      if (this.placeDossierTypeAvailable.length && this.dossierId)
        group.push({
          id: 'dossier_place',
          icon: ['fal', 'folder'],
          label: this.$t('global.dossier_place')
        })

      if (this.placeCompanyAvailable.length)
        group.push({
          id: 'company_place',
          icon: ['fal', 'hotel'],
          label: this.$t('global.company_place')
        })

      if (this.wikiPlaceAvailable.length)
        group.push({
          id: 'wiki_place',
          icon: ['fal', 'database'],
          label: 'WIKI'
        })

      if (this.otherPlaceAvailable.length)
        group.push({
          id: 'other_place',
          label: this.$t('global.other_place'),
          icon: ['fal', 'map-marker-alt']
        })

      return group
    },
    localPlaceId: {
      get() {
        return this.placeId
      },
      set(value) {
        this.$emit('update:placeId', value)
      }
    },
    localPlaceType: {
      get() {
        return this.placeType
      },
      set(value) {
        this.$emit('update:placeType', value)
      }
    },
    localPlaceModel: {
      get() {
        return this.placeModel
      },
      set(value) {
        this.$emit('update:placeModel', value)
      }
    },
    localPlaceOther: {
      get() {
        return this.placeOther
      },
      set(value) {
        this.$emit('update:placeOther', value)
      }
    },
    localPlaceStreet: {
      get() {
        return this.placeStreet
      },
      set(value) {
        this.$emit('update:placeStreet', value)
      }
    },
    localPlaceExtraStreet: {
      get() {
        return this.placeExtraStreet
      },
      set(value) {
        this.$emit('update:placeExtraStreet', value)
      }
    },
    localPlaceCity: {
      get() {
        return this.placeCity
      },
      set(value) {
        this.$emit('update:placeCity', value)
      }
    },
    localPlaceCityId: {
      get() {
        return this.placeCityId
      },
      set(value) {
        this.$emit('update:placeCityId', value)
      }
    },
    localPlaceCityObject: {
      get() {
        return this.placeCityObject
      },
      set(value) {
        this.$emit('update:placeCityObject', value)
      }
    },
    localPlaceZip: {
      get() {
        return this.placeZip
      },
      set(value) {
        this.$emit('update:placeZip', value)
      }
    },
    localPlaceCountry: {
      get() {
        return this.placeCountry
      },
      set(value) {
        this.$emit('update:placeCountry', value)
      }
    }
  },
  watch: {
    defaultPlaceGroup(newValue, oldValue) {
      if (newValue && !this.selectedPlaceGroup) {
        this.selectedPlaceGroup = newValue
      }
    },
    defaultPlaceType(newValue, oldValue) {
      if (newValue) {
        if (
          this.defaultPlaceGroup === 'wiki_place' &&
          [
            PlaceType.CIMETIERE,
            PlaceType.CREMATORIUM,
            PlaceType.HOPITAL,
            PlaceType.MAISON_DE_REPOS,
            PlaceType.SALLE_CREMATORIUM,
            PlaceType.ADMINISTRATION_COMMUNALE,
            PlaceType.EGLISE
          ].includes(newValue)
        ) {
          this.selectedPlaceType = newValue
        } else if (
          this.defaultPlaceGroup === 'company_place' &&
          [
            PlaceType.FUNERARIUM,
            PlaceType.SALLE_FUNERARIUM,
            PlaceType.SALLE_DE_RECEPTION,
            PlaceType.SALLE_DE_CEREMONIE
          ].includes(newValue)
        ) {
          this.selectedPlaceType = newValue
        } else if (
          this.defaultPlaceGroup === 'other_place' &&
          [PlaceType.RUE, PlaceType.AUTRES].includes(newValue)
        ) {
          this.selectedPlaceType = newValue
        }
      }
    },
    isModalOpen(newValue, _oldValue) {
      this.$emit('update:isModalOpen', newValue)
      if (newValue) {
        this.selectedPlaceGroup = this.defaultPlaceGroup
        this.selectedPlaceType = this.defaultPlaceType
      }
    }
  },
  methods: {
    ...mapActions({
      processStorePlaceAction: 'wiki/storePlace'
    }),
    close(state) {
      setTimeout(() => this.$emit('update:isModalOpen', false), 100)
    },
    async saveNewWikiPlace() {
      if (this.savingNewWikiPlace) return

      this.savingNewWikiPlace = true

      try {
        const response = await this.processStorePlaceAction({
          place: this.newWikiPlace
        })

        // The API answers 200 with `success: false` when its transaction is
        // rolled back, so a rejected promise is not the only failure path.
        if (response?.success === false) {
          this.$h.toastDanger(this.$t('toasts.server_error'), response.message)

          return
        }

        this.$h.toastSuccess(
          this.$t('toasts.created_m', { var: this.$t('attributes.place') })
        )

        // `type` follows the search type of the wiki tab: keep it, or the
        // "add a wiki place" button stays disabled for the next creation.
        this.newWikiPlace = {
          ...emptyWikiPlace(this.$i18n.locale),
          type: this.newWikiPlace.type
        }
        this.addWikiPlaceTab = false
        this.$refs.wikiPlaceTab?.refreshSearch?.()
      } catch (error) {
        this.$h.toastDanger(this.$t('toasts.server_error'))
      } finally {
        this.savingNewWikiPlace = false
      }
    }
  }
}
</script>
